import React, {useEffect, useState} from "react";
import {Box, Checkbox, LoadingDecorator} from "@renta-apps/renta-react-components";
import Localizer from "@/localization/Localizer";
import styles from "./EquipmentsGrid.module.scss";
import EquipmentsGridRow from "@/pages/ConstructionSiteDetails/EquipmentsNew/EquipmentsGrid/EquipmentsGridRow";
import {getProductWithTelematicsDetails, getProductsWithTelematicsDetails} from "@/services/ConstructionSiteService";
import {ProductWithTelematics} from "@/models/server/Models/Equipment/ProductWithTelematics";
import {ServiceType} from "@/models/Enums";
import {ProductWithTelematicsDetails} from "@/models/server/Models/Equipment/ProductWithTelematicsDetails";

interface IEquipmentsGridProps {
    constructionSiteId: string;
    isLoading: boolean;
    data: ProductWithTelematics[];
    canSelectChargingRequest: boolean;
    isEasyPlusUser: boolean;
    onSubscribeButtonClick: (item: ProductWithTelematics) => void;
    onReturnButtonClick: (item: ProductWithTelematics) => void;
    onServiceRequestButtonClick: (item: ProductWithTelematics, serviceType: ServiceType) => void;
    gridRef: React.RefObject<HTMLDivElement>;
}

const EquipmentsGrid: React.FC<IEquipmentsGridProps> = ({
    constructionSiteId,
    isLoading,
    data,
    canSelectChargingRequest,
    isEasyPlusUser,
    onSubscribeButtonClick,
    onReturnButtonClick,
    onServiceRequestButtonClick,
    gridRef,
}) => {
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [equipmentDetailsMap, setEquipmentDetailsMap] = useState<{ [key: string]: ProductWithTelematicsDetails | null }>({});

    useEffect(() => {
        // Reset expanded items when data changes
        if (isLoading) {
            setExpandedItems(new Set());
            setEquipmentDetailsMap({});
        }
    }, [isLoading]);

    const handleHeaderCheckboxClick = () => {
    };

    const handleHeaderAccordionClick = async () => {
        const allExpanded = expandedItems.size === data.length;

        if (allExpanded) {
            // Collapse all items
            setExpandedItems(new Set());

            return;
        }

        // Expand all items
        const newExpandedItems = new Set(data.map(item => item.internalId));
        setExpandedItems(newExpandedItems);

        // Fetch device details for all items that are not already in the map
        const fetchRequest = data
            .filter((item) => !equipmentDetailsMap[item.internalId])
            .map(x => ({
                internalId: x.internalId,
                rentaId: x.rentaId,
                constructionSiteId: constructionSiteId,
            }))

        if (!fetchRequest.length) {
            return;
        }

        try {
            const details = await getProductsWithTelematicsDetails(fetchRequest);
            const detailsMap = details.reduce((map, detail) => {
                map[detail.internalId] = detail;
                return map;
            }, {} as { [key: string]: ProductWithTelematicsDetails });
            setEquipmentDetailsMap(prevDetails => ({...prevDetails, ...detailsMap}));
        } catch (error) {
            console.error('Error loading equipment details:', error);
            const detailsMap = fetchRequest.reduce((map, detail) => {
                map[detail.internalId] = null;
                return map;
            }, {} as { [key: string]: ProductWithTelematicsDetails | null });
            setEquipmentDetailsMap(prevDetails => ({...prevDetails, ...detailsMap}));
        }
    };

    const handleAccordionButtonClick = async (item: ProductWithTelematics) => {
        const key = item.internalId;
        setExpandedItems(prevExpandedItems => {
            const newExpandedItems = new Set(prevExpandedItems);
            if (newExpandedItems.has(key)) {
                newExpandedItems.delete(key);
            } else {
                newExpandedItems.add(key);
            }
            return newExpandedItems;
        });

        if (!equipmentDetailsMap[key]) {
            try {
                const details = await getProductWithTelematicsDetails(item.internalId, item.rentaId, constructionSiteId);
                setEquipmentDetailsMap(prevDetails => ({...prevDetails, [key]: details}));
            } catch (error) {
                console.error('Error loading equipment details:', error);
                setEquipmentDetailsMap(prevDetails => ({...prevDetails, [key]: null}));
            }
        }
    };

    return (
        <div className={styles.dataTable} data-cy="equipments-grid" ref={gridRef}>
            {/* Header Row */}
            <div className={`${styles.gridRow} ${styles.dataRow} ${styles.header}`}>
                <div className={styles.name}>
                    <div className={styles.checkboxCell}>
                        <Checkbox checked={false} onChange={handleHeaderCheckboxClick}/>
                    </div>
                    <div>{Localizer.fleetMonitoringPageGridDeviceName}</div>
                </div>
                <div className={styles.rentaId}>{Localizer.fleetMonitoringPageGridId}</div>
                <div className={styles.actions}>{Localizer.fleetMonitoringPageGridActions}</div>
                <div className={`${styles.gridItem} ${styles.expand}`}>
                    <div className={styles.accordionButton} onClick={handleHeaderAccordionClick} data-cy={`accordion-button-header`}>
                        <i className={`fas fa-angle-down ${(data.length > 0 && expandedItems.size === data.length) ? styles.rotated : ''}`}></i>
                    </div>
                </div>
            </div>

            {/* Loading Rows */}
            {isLoading && [...Array(4)].map((_, index) => (
                <LoadingDecorator key={index} className={styles.loaderContainer} hideContentOnLoading={false} isLoading={isLoading}/>
            ))}

            {/* Display information about no data */}
            {!isLoading && data.length === 0 &&
                <Box display="flex" alignItems="center" justifyContent="center" className={styles.loaderContainer}>
                    {Localizer.fleetMonitoringPageGridEmpty}
                </Box>
            }

            {/* Data Rows */}
            {!isLoading && data.map((item, index) => (
                <EquipmentsGridRow item={item}
                                   index={index}
                                   key={item.internalId}
                                   expanded={expandedItems.has(item.internalId)}
                                   equipmentDetails={equipmentDetailsMap[item.internalId]}
                                   canSelectChargingRequest={canSelectChargingRequest}
                                   isEasyPlusUser={isEasyPlusUser}
                                   onAccordionButtonClick={() => handleAccordionButtonClick(item)}
                                   onSubscribeButtonClick={() => onSubscribeButtonClick(item)}
                                   onReturnButtonClick={() => onReturnButtonClick(item)}
                                   onServiceRequestButtonClick={(serviceType) => onServiceRequestButtonClick(item, serviceType)}
                />
            ))}
        </div>
    );
};

export default EquipmentsGrid;